import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchPage, fetchFaq, fetchTeam } from '../api/public'
import { CmsImage } from '../components/CmsImage'
import { useCrtPageTitle } from '../hooks/useCrtPageTitle'
import { usePageMeta } from '../hooks/usePageMeta'
import { cmsImageSocialUrl, cmsImageUrl } from '../lib/cms-image'
import { schemaAboutPage, schemaGraph } from '../lib/schema-org'

export function AboutPage() {
  const pageQ = useQuery({
    queryKey: ['page', 'ueber-uns'],
    queryFn: () => fetchPage('ueber-uns'),
    retry: false,
  })
  const teamQ = useQuery({
    queryKey: ['public-team'],
    queryFn: fetchTeam,
  })
  const faqQ = useQuery({
    queryKey: ['faq'],
    queryFn: () => fetchFaq().then((r) => r.faq),
  })

  const title = pageQ.data?.title || 'Über uns'
  useCrtPageTitle(title)

  const team = teamQ.data?.team ?? []
  const faqItems = faqQ.data ?? []
  const jsonLd = useMemo(
    () =>
      schemaGraph(
        ...schemaAboutPage({
          title,
          description: 'Über BNKR — Bunker Events, Label und Verein aus der Ostschweiz.',
          heroImage: pageQ.data?.heroImageUrl,
          team: team.map((m) => ({ displayName: m.displayName, photoUrl: m.photoUrl })),
          faq: faqItems,
        }),
      ),
    [title, pageQ.data?.heroImageUrl, team, faqItems],
  )

  usePageMeta({
    title,
    description: 'Über BNKR — Team, Verein, Events und Label.',
    canonical: '/about',
    ogImage: cmsImageSocialUrl(pageQ.data?.heroImageUrl) ?? pageQ.data?.heroImageUrl,
    jsonLd,
  })

  useEffect(() => {
    if (window.location.hash !== '#team') return
    if (!teamQ.isSuccess) return
    const t = window.setTimeout(() => {
      document.getElementById('team')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
    return () => window.clearTimeout(t)
  }, [teamQ.isSuccess])

  return (
    <section className="section" style={{ paddingBottom: 'var(--sp-16)', paddingTop: 'var(--sp-4)' }}>
      <div className="wrap">
        <section id="about">
          <div className="kicker crt">↳ /about</div>
          <h1 className="display h2" style={{ marginTop: 14 }}>
            {title}
          </h1>

          {pageQ.isError && (
            <p className="muted" style={{ marginTop: 24 }}>
              Inhalt folgt — die Seite «ueber-uns» ist in der Datenbank noch nicht publiziert.
            </p>
          )}

          <div className="grid-2" style={{ marginTop: 48, alignItems: 'start' }}>
            <div>
              {pageQ.data?.heroImageUrl ? (
                <CmsImage
                  src={pageQ.data.heroImageUrl}
                  size="lg"
                  alt=""
                  loading="eager"
                  width={1120}
                  height={630}
                  sizes="(max-width: 1120px) 100vw, 1120px"
                  style={{ width: '100%', marginBottom: 24, border: '1px solid var(--line)' }}
                />
              ) : null}
              {pageQ.data?.bodyHtml ? (
                <div className="rich-text rich-text--paper" dangerouslySetInnerHTML={{ __html: pageQ.data.bodyHtml }} />
              ) : null}
            </div>
          </div>
        </section>

        <section id="team" style={{ marginTop: 'var(--sp-16)', scrollMarginTop: 'calc(var(--crt-chrome-h) + 24px)' }}>
          <div className="sect-head">
            <div>
              <div className="kicker crt">↳ /team</div>
              <h2 className="display h2" style={{ marginTop: 14 }}>
                Unser Team
              </h2>
            </div>
            <div className="lead" style={{ fontSize: 18, maxWidth: '50ch' }}>
              DJs, Booker, Veranstalter — eine Crew.
            </div>
          </div>
          {teamQ.isError ? <p className="error">Team konnte nicht geladen werden.</p> : null}
          {teamQ.data?.team?.length ? (
            <div className="team">
              {[...new Map(teamQ.data.team.map((m) => [m.id, m])).values()].map((m) => (
                <article key={m.id} className="member">
                  <div
                    className="ph"
                    style={
                      m.photoUrl
                        ? {
                            backgroundImage: `url(${cmsImageUrl(m.photoUrl, 'thumb') ?? m.photoUrl})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }
                        : undefined
                    }
                  />
                  <div className="nm">{m.displayName}</div>
                </article>
              ))}
            </div>
          ) : teamQ.isSuccess ? (
            <p className="muted">Noch keine Team-Einträge.</p>
          ) : null}
        </section>

        {faqQ.data?.length ? (
          <section style={{ marginTop: 'var(--sp-16)' }}>
            <div className="kicker crt">↳ /faq</div>
            <h2 className="display h2" style={{ marginTop: 14 }}>
              FAQ
            </h2>
            <div className="faq-list" style={{ marginTop: 32 }}>
              {[...new Map(faqQ.data.map((item) => [item.id, item])).values()].map((item) => (
                <details key={item.id} className="faq-item">
                  <summary className="faq-q">
                    {item.question} <span className="plus">+</span>
                  </summary>
                  <div className="faq-a rich-text rich-text--paper" dangerouslySetInnerHTML={{ __html: item.answerHtml }} />
                </details>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </section>
  )
}
